import { useState, useEffect, useCallback } from 'react'
import { FileText, UserCheck, AlertCircle, CalendarClock, RefreshCw, Search } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { QueueCard } from './analytics/QueueCard'
import { ActivityFeed } from './analytics/ActivityFeed'
import {
  getOperationsOverview,
  getActivityFeed,
  getCustomerList,
} from '@/src/lib/api/admin-analytics'
import type {
  OperationsOverview,
  ActivityFeedItem,
  AdminCustomerListResponse,
} from '@/src/types/admin/analytics'

const POLL_MS = 2 * 60 * 1000 // 2 minutes

export default function AdminOperationsDashboard() {
  const [overview, setOverview] = useState<OperationsOverview | null>(null)
  const [feed, setFeed] = useState<ActivityFeedItem[]>([])
  const [customers, setCustomers] = useState<AdminCustomerListResponse | null>(null)
  const [overviewLoading, setOverviewLoading] = useState(true)
  const [feedLoading, setFeedLoading] = useState(true)
  const [customersLoading, setCustomersLoading] = useState(true)

  // Customer list filters
  const [search, setSearch] = useState('')
  const [packageFilter, setPackageFilter] = useState('')
  const [kycFilter, setKycFilter] = useState('')
  const [page, setPage] = useState(1)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true)
    try {
      const data = await getOperationsOverview()
      setOverview(data)
    } finally {
      setOverviewLoading(false)
    }
  }, [])

  const fetchFeed = useCallback(async () => {
    setFeedLoading(true)
    try {
      const data = await getActivityFeed(30)
      setFeed(data)
      setLastUpdated(new Date())
    } finally {
      setFeedLoading(false)
    }
  }, [])

  const fetchCustomers = useCallback(async () => {
    setCustomersLoading(true)
    try {
      const data = await getCustomerList({
        page,
        limit: 20,
        search: search || undefined,
        package_code: packageFilter || undefined,
        kyc_status: kycFilter || undefined,
      })
      setCustomers(data)
    } finally {
      setCustomersLoading(false)
    }
  }, [page, search, packageFilter, kycFilter])

  useEffect(() => {
    fetchOverview()
    fetchFeed()
    const id = setInterval(() => { fetchOverview(); fetchFeed() }, POLL_MS)
    return () => clearInterval(id)
  }, [fetchOverview, fetchFeed])

  useEffect(() => {
    fetchCustomers()
  }, [fetchCustomers])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    fetchCustomers()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">ภาพรวมการดำเนินงาน</h1>
          <p className="text-muted-foreground">คิวงาน กิจกรรมล่าสุด และจัดการลูกค้า</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-muted-foreground">
              อัพเดตล่าสุด {lastUpdated.toLocaleTimeString('th-TH')}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchOverview(); fetchFeed() }}
            disabled={overviewLoading || feedLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${overviewLoading ? 'animate-spin' : ''}`} />
            รีเฟรช
          </Button>
        </div>
      </div>

      {/* Queue Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <QueueCard
          title="สลิปชำระเงินรอตรวจ"
          count={overview?.payment_slips_queue.total ?? 0}
          urgentCount={overview?.payment_slips_queue.under_review ?? 0}
          icon={FileText}
          loading={overviewLoading && !overview}
        />
        <QueueCard
          title="KYC รอดำเนินการ"
          count={overview?.kyc_queue.total ?? 0}
          icon={UserCheck}
          loading={overviewLoading && !overview}
        />
        <QueueCard
          title="เครดิตใกล้หมด"
          count={overview?.low_credits.count ?? 0}
          icon={AlertCircle}
          loading={overviewLoading && !overview}
        />
        <QueueCard
          title="แพ็กเกจใกล้หมดอายุ"
          count={overview?.expiring_packages.length ?? 0}
          icon={CalendarClock}
          loading={overviewLoading && !overview}
        />
      </div>

      {/* Activity Feed + Expiring Table */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">กิจกรรมล่าสุด</CardTitle>
          </CardHeader>
          <CardContent>
            <ActivityFeed items={feed} loading={feedLoading && feed.length === 0} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">แพ็กเกจใกล้หมดอายุ (14 วัน)</CardTitle>
          </CardHeader>
          <CardContent>
            {overviewLoading && !overview ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : overview?.expiring_packages.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">ไม่มีแพ็กเกจใกล้หมดอายุ</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ลูกค้า</TableHead>
                    <TableHead>แพ็กเกจ</TableHead>
                    <TableHead className="text-right">วันที่เหลือ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overview?.expiring_packages.slice(0, 8).map((p) => (
                    <TableRow key={p.customer_id}>
                      <TableCell className="text-xs truncate max-w-[140px]">{p.email}</TableCell>
                      <TableCell>{p.package_code}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={p.days_until_expiry <= 3 ? 'destructive' : 'secondary'}>
                          {p.days_until_expiry} วัน
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Customer Management Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">จัดการลูกค้า</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <form onSubmit={handleSearch} className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="ค้นหาอีเมล / ชื่อธุรกิจ..."
                className="pl-8"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={packageFilter} onValueChange={(v) => { setPackageFilter(v === 'all' ? '' : v); setPage(1) }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="แพ็กเกจ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทั้งหมด</SelectItem>
                <SelectItem value="FREE">FREE</SelectItem>
                <SelectItem value="STARTER">STARTER</SelectItem>
                <SelectItem value="PROFESSIONAL">PROFESSIONAL</SelectItem>
                <SelectItem value="ENTERPRISE">ENTERPRISE</SelectItem>
              </SelectContent>
            </Select>
            <Select value={kycFilter} onValueChange={(v) => { setKycFilter(v === 'all' ? '' : v); setPage(1) }}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="KYC" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทั้งหมด</SelectItem>
                <SelectItem value="pending">รอดำเนินการ</SelectItem>
                <SelectItem value="approved">อนุมัติแล้ว</SelectItem>
                <SelectItem value="rejected">ปฏิเสธ</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" size="sm">ค้นหา</Button>
          </form>

          {/* Table */}
          {customersLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>อีเมล</TableHead>
                    <TableHead>แพ็กเกจ</TableHead>
                    <TableHead>KYC</TableHead>
                    <TableHead className="text-right">เครดิต</TableHead>
                    <TableHead>สถานะ</TableHead>
                    <TableHead>สมัครเมื่อ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers?.data.map((c) => (
                    <TableRow key={c.customer_id}>
                      <TableCell>
                        <div className="text-sm truncate max-w-[180px]">{c.email}</div>
                        {c.business_name && (
                          <div className="text-xs text-muted-foreground truncate max-w-[180px]">
                            {c.business_name}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{c.package_code}</TableCell>
                      <TableCell>
                        {c.kyc_status ? (
                          <Badge
                            variant={
                              c.kyc_status === 'approved'
                                ? 'secondary'
                                : c.kyc_status === 'rejected'
                                  ? 'destructive'
                                  : 'outline'
                            }
                            className="text-xs"
                          >
                            {c.kyc_status === 'approved' ? 'อนุมัติ' : c.kyc_status === 'rejected' ? 'ปฏิเสธ' : 'รอ'}
                          </Badge>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-right">{c.credit_balance.toLocaleString()}</TableCell>
                      <TableCell>
                        <Badge variant={c.is_active ? 'secondary' : 'outline'} className="text-xs">
                          {c.is_active ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(c.created_at).toLocaleDateString('th-TH')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {customers && customers.pagination.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>
                    {customers.pagination.total.toLocaleString()} รายการ
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      ก่อนหน้า
                    </Button>
                    <span className="flex items-center px-2">
                      {page} / {customers.pagination.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(customers.pagination.total_pages, p + 1))}
                      disabled={page === customers.pagination.total_pages}
                    >
                      ถัดไป
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
